/**
 * Batch Helper Utilities
 * Contains validation functions and helper utilities for batch management
 */

// Constants
export const IN_PROGRESS_LOT_IDS_KEY = 'LotIds';

/**
 * Validates if a value is a positive integer (>0)
 * @param {number} value - The value to validate
 * @returns {boolean} - True if value is a positive integer
 */
export function isPositiveInteger(value) {
  return typeof value === 'number' && Number.isInteger(value) && value > 0;
}

/**
 * Validates if a value is a non-negative integer (>=0)
 * @param {number} value - The value to validate
 * @returns {boolean} - True if value is a non-negative integer
 */
export function isNonNegativeInteger(value) {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

/**
 * Calculates the next batch number for a lot
 * @param {Object} lot - The lot object containing existing batches
 * @returns {number} - The next batch number
 */
export const calculateNextBatchNumber = (lot) => {
  if (!lot?.batch?.length) return 1;

  const maxBatchNum = Math.max(
    ...lot.batch.map(batch => {
      const parts = batch?.name?.split('-') || [];
      const number = parseInt(parts[1], 10);
      return !isNaN(number) && number > 0 ? number : 0;
    }),
    0 // Ensure Math.max has at least one value
  );
  return maxBatchNum + 1;
};

/**
 * Calculates available sewing capacity for a lot
 * @param {Object} lot - The lot object
 * @param {number} totalSewDamagedQty - Total damaged sewing quantity
 * @returns {number} - Available sewing capacity (never negative)
 */
export const getAvailableSewCapacity = (lot, totalSewDamagedQty = 0) => {
  if (!lot || typeof lot.total_cut_qty !== 'number') return 0;
  return Math.max(0, (lot.total_cut_qty || 0) - (lot.total_sew_qty || 0) - totalSewDamagedQty);
};

/**
 * Calculates total sew damaged quantity across all batches
 * @param {Array} batches - Array of batch objects
 * @returns {number} - Total sew damaged quantity
 */
export const calculateTotalSewDamagedQty = (batches) => {
  return batches?.reduce((sum, batch) => sum + (batch?.sew_damage_qty || 0), 0) || 0;
};
